/** 修正依頼の一覧(未対応 / 対応済みの切替 + 対象種別での絞り込み)。 */
import { useCallback, useEffect, useState } from 'react';
import {
  REQUEST_KIND_LABELS,
  REQUEST_STATUS_LABELS,
  REQUEST_TARGET_TYPES,
  REQUEST_TARGET_TYPE_LABELS,
  type ContentRequest,
  type ContentRequestService,
  type RequestStatus,
  type RequestTargetType,
} from '../services/requests';

interface Props {
  readonly service: ContentRequestService;
  /** 依頼が追加されたら親から増やしてもらい、再取得のトリガにする。 */
  readonly reloadKey: number;
}

function formatDate(date: Date | null): string {
  if (!date) return '—';
  return date.toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' });
}

export function RequestsPanel({ service, reloadKey }: Props) {
  const [items, setItems] = useState<readonly ContentRequest[] | null>(null);
  const [status, setStatus] = useState<RequestStatus>('open');
  /** 空文字 = すべての対象種別。 */
  const [targetType, setTargetType] = useState<RequestTargetType | ''>('');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      setItems(await service.list());
    } catch (cause) {
      setError(`一覧を取得できませんでした: ${String(cause)}`);
    }
  }, [service]);

  useEffect(() => {
    void reload();
  }, [reload, reloadKey]);

  const all = items ?? [];
  const visible = all.filter(
    (item) => item.status === status && (targetType === '' || item.targetType === targetType),
  );

  /** 対象種別ごとの件数(いま選んでいる状態のぶんだけ)。 */
  function countOf(type: RequestTargetType): number {
    return all.filter((item) => item.status === status && item.targetType === type).length;
  }

  return (
    <div className="workspace">
      <div className="pane pane--full">
        <div className="toolbar">
          <div className="tabs">
            {(['open', 'done'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`tab${status === value ? ' tab--active' : ''}`}
                onClick={() => {
                  setStatus(value);
                }}
              >
                {REQUEST_STATUS_LABELS[value]}
              </button>
            ))}
          </div>
          <select
            className="select"
            style={{ maxWidth: '220px' }}
            value={targetType}
            onChange={(event) => {
              setTargetType(event.target.value as RequestTargetType | '');
            }}
          >
            <option value="">対象すべて</option>
            {REQUEST_TARGET_TYPES.map((value) => (
              <option key={value} value={value}>
                {REQUEST_TARGET_TYPE_LABELS[value]}({String(countOf(value))})
              </option>
            ))}
          </select>
          <button
            className="button button--small"
            type="button"
            onClick={() => {
              void reload();
            }}
          >
            再読込
          </button>
          <span className="muted mono">{String(visible.length)} 件</span>
        </div>

        <div className="notice">
          ここに貯まったメモは、開発側が content のソース(fixes.csv 等)に適用して再ビルドします。
          反映が済んだものは「対応済みにする」を押してください。
        </div>

        {error !== null && (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        )}

        {items === null ? (
          <p className="muted">読み込み中…</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '130px' }}>日時</th>
                <th style={{ width: '100px' }}>対象種別</th>
                <th style={{ width: '150px' }}>対象</th>
                <th style={{ width: '140px' }}>種別</th>
                <th>内容</th>
                <th style={{ width: '130px' }}>状態</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.id}>
                  <td className="mono muted">{formatDate(item.createdAt)}</td>
                  <td>
                    <span className={`tag tag--${item.targetType}`}>
                      {REQUEST_TARGET_TYPE_LABELS[item.targetType]}
                    </span>
                    {item.band !== '' && <div className="muted mono">{item.band}</div>}
                  </td>
                  <td>
                    <div className="mono">{item.targetId}</div>
                    <div className="muted">{item.targetLabel}</div>
                  </td>
                  <td>{REQUEST_KIND_LABELS[item.kind]}</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{item.body}</td>
                  <td>
                    <span className={`badge badge--${item.status}`}>
                      {REQUEST_STATUS_LABELS[item.status]}
                    </span>
                    <br />
                    <button
                      className="button button--small"
                      type="button"
                      onClick={() => {
                        void service
                          .setStatus(item.id, item.status === 'open' ? 'done' : 'open')
                          .then(reload)
                          .catch((cause: unknown) => {
                            setError(`更新に失敗しました: ${String(cause)}`);
                          });
                      }}
                    >
                      {item.status === 'open' ? '対応済みにする' : '未対応に戻す'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {items !== null && visible.length === 0 && (
          <p className="muted">該当する依頼はありません。</p>
        )}
      </div>
    </div>
  );
}
